import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Clock } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { articles as staticArticles, media } from "@/i18n/translations";
import SEO from "@/components/SEO";
import { FadeUp, PageHeader } from "@/components/ui-primitives";
import ArticlePreviewImage from "@/components/ArticlePreviewImage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const normalizeDbArticle = (a) => ({
  slug: a.slug,
  date: a.date,
  readTime: a.read_time ?? a.readTime ?? 5,
  category: a.category || { en: "", ru: "", tr: "" },
  image: a.image || "",
  _source: "db",
  en: a.en || { title: "", excerpt: "", body: [] },
  ru: a.ru || { title: "", excerpt: "", body: [] },
  tr: a.tr || { title: "", excerpt: "", body: [] },
});

const formatDate = (d, lang) =>
  new Date(d).toLocaleDateString(lang === "ru" ? "ru-RU" : "en-GB", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const News = () => {
  const { t, lang } = useLanguage();
  const [dbArticles, setDbArticles] = useState([]);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/articles`)
      .then((res) => {
        if (mounted) setDbArticles((res.data.items || []).map(normalizeDbArticle));
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  // Merge DB + static, de-dupe by slug (DB wins), sort newest first
  const merged = useMemo(() => {
    const bySlug = new Map();
    [...dbArticles, ...staticArticles].forEach((a) => {
      if (!bySlug.has(a.slug)) bySlug.set(a.slug, a);
    });
    return Array.from(bySlug.values()).sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [dbArticles]);

  const featured = merged[0];
  const secondary = merged.slice(1, 3);
  const archive = merged.slice(3);

  return (
    <>
      <SEO
        path="/insights"
        title={lang === "ru" ? "Аналитика и брифы" : "Insights & Briefings"}
        description={
          lang === "ru"
            ? "Материалы для капитанов и менеджеров яхт — оформление, порты, регулирование и круизы в Турции."
            : "Briefings for captains and yacht managers — clearance, ports, regulations and cruising in Türkiye."
        }
      />
      <PageHeader
        eyebrow={t.newsPage.eyebrow}
        title={t.newsPage.title}
        intro={t.newsPage.intro}
        image="/assets/photos/insights-hero.jpg"
        testId="news-header"
      />

      <section className="py-20 px-6 sm:px-10 lg:px-16" data-testid="news-list">
        <div className="max-w-7xl mx-auto">
          {/* ---------- Featured (latest) ---------- */}
          {featured && (
            <FadeUp>
              <Link
                to={`/insights/${featured.slug}`}
                className="group block mb-20 md:mb-24"
                data-testid={`article-card-${featured.slug}`}
              >
                <div className="grid md:grid-cols-12 gap-8 lg:gap-12 items-center">
                  <div className="md:col-span-7 relative overflow-hidden aspect-[16/10] chart-frame">
                    <ArticlePreviewImage
                      src={featured.image}
                      alt={(featured[lang] || featured.en).title}
                      imgClassName="transition-transform duration-[1400ms] group-hover:scale-[1.04]"
                    />
                    <div className="absolute top-5 left-8 z-10">
                      <span className="inline-flex items-center gap-2 bg-navy/90 text-white px-4 py-2 text-[10px] tracking-[0.3em] uppercase">
                        <span className="w-1.5 h-1.5 bg-brand rounded-full" />
                        {lang === "ru" ? "Главная статья" : lang === "tr" ? "Öne çıkan" : "Featured"}
                      </span>
                    </div>
                  </div>
                  <div className="md:col-span-5">
                    <div className="flex items-center gap-3 text-[11px] tracking-[0.25em] uppercase text-brand/80 mb-5">
                      <span>{featured.category?.[lang] || featured.category?.en}</span>
                      <span className="w-6 h-px bg-brand/40" />
                      <span>{formatDate(featured.date, lang)}</span>
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-navy leading-[1.08] group-hover:text-brand transition-colors">
                      {(featured[lang] || featured.en).title}
                    </h2>
                    <p className="mt-5 text-navy/70 text-base lg:text-lg leading-relaxed">
                      {(featured[lang] || featured.en).excerpt}
                    </p>
                    <div className="mt-7 flex items-center gap-6 text-[11px] tracking-[0.2em] uppercase text-navy/50">
                      <span className="flex items-center gap-1.5">
                        <Clock size={11} /> {featured.readTime} min read
                      </span>
                    </div>
                    <div className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-brand font-semibold group-hover:gap-4 transition-all">
                      {t.common.readMore} <ArrowRight size={14} />
                    </div>
                  </div>
                </div>
              </Link>
            </FadeUp>
          )}

          {/* ---------- Secondary pair ---------- */}
          {secondary.length > 0 && (
            <div className="grid md:grid-cols-2 gap-10 lg:gap-14 mb-20 md:mb-24">
              {secondary.map((a, i) => (
                <FadeUp key={a.slug} delay={i * 0.08}>
                  <Link
                    to={`/insights/${a.slug}`}
                    className="group block"
                    data-testid={`article-card-${a.slug}`}
                  >
                    <div className="relative aspect-[5/3] overflow-hidden mb-6 chart-frame-md">
                      <ArticlePreviewImage
                        src={a.image}
                        alt={(a[lang] || a.en).title}
                        imgClassName="transition-transform duration-[1200ms] group-hover:scale-105"
                      />
                    </div>
                    <div className="flex items-center gap-3 text-[11px] tracking-[0.25em] uppercase text-brand/80 mb-3">
                      <span>{a.category?.[lang] || a.category?.en}</span>
                      <span className="w-1 h-1 bg-brand/70 rounded-full" />
                      <span>{formatDate(a.date, lang)}</span>
                      <span className="w-1 h-1 bg-brand/70 rounded-full" />
                      <span className="flex items-center gap-1">
                        <Clock size={10} /> {a.readTime} min
                      </span>
                    </div>
                    <h3 className="font-display text-2xl sm:text-3xl text-navy leading-[1.12] group-hover:text-brand transition-colors">
                      {(a[lang] || a.en).title}
                    </h3>
                    <p className="mt-4 text-navy/70 text-base leading-relaxed line-clamp-3">
                      {(a[lang] || a.en).excerpt}
                    </p>
                  </Link>
                </FadeUp>
              ))}
            </div>
          )}

          {/* ---------- Archive (list) ---------- */}
          {archive.length > 0 && (
            <div className="border-t border-navy/15 pt-12">
              <div className="flex items-baseline justify-between mb-10">
                <div className="overline" style={{ color: "#C9A24A" }}>
                  {lang === "ru" ? "Архив" : lang === "tr" ? "Arşiv" : "Archive"}
                </div>
                <div className="text-[11px] tracking-[0.25em] uppercase text-navy/45">
                  {archive.length} {lang === "ru" ? "статей" : lang === "tr" ? "yazı" : "briefings"}
                </div>
              </div>
              <ul className="divide-y divide-navy/10" data-testid="archive-list">
                {archive.map((a, i) => (
                  <FadeUp key={a.slug} delay={i * 0.04}>
                    <li>
                      <Link
                        to={`/insights/${a.slug}`}
                        className="group grid grid-cols-12 gap-5 sm:gap-8 py-7 items-center"
                        data-testid={`article-card-${a.slug}`}
                      >
                        {/* Thumbnail */}
                        <div className="relative col-span-4 sm:col-span-3 aspect-[5/4] overflow-hidden chart-frame-sm">
                          <ArticlePreviewImage
                            src={a.image}
                            alt={(a[lang] || a.en).title}
                            imgClassName="transition-transform duration-[1200ms] group-hover:scale-105"
                          />
                        </div>
                        {/* Meta + title */}
                        <div className="col-span-8 sm:col-span-7">
                          <div className="flex items-center gap-3 text-[10px] tracking-[0.25em] uppercase text-brand/80 mb-2 flex-wrap">
                            <span>{a.category?.[lang] || a.category?.en}</span>
                            <span className="hidden sm:inline w-1 h-1 bg-brand/70 rounded-full" />
                            <span className="hidden sm:inline">{formatDate(a.date, lang)}</span>
                            <span className="hidden sm:inline w-1 h-1 bg-brand/70 rounded-full" />
                            <span className="hidden sm:flex items-center gap-1">
                              <Clock size={10} /> {a.readTime} min
                            </span>
                          </div>
                          <h4 className="font-display text-lg sm:text-2xl text-navy leading-[1.18] group-hover:text-brand transition-colors">
                            {(a[lang] || a.en).title}
                          </h4>
                          <div className="sm:hidden mt-2 text-[10px] tracking-[0.2em] uppercase text-navy/50">
                            {formatDate(a.date, lang)} · {a.readTime} min
                          </div>
                        </div>
                        {/* Arrow */}
                        <div className="hidden sm:flex col-span-2 justify-end text-navy/30 group-hover:text-brand transition-colors">
                          <ArrowRight
                            size={22}
                            className="transition-transform group-hover:translate-x-2"
                          />
                        </div>
                      </Link>
                    </li>
                  </FadeUp>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default News;
